import {
  pointChange,
  relativeChange,
  type DashboardPeriod,
} from "@estelle/domain";

/**
 * How the dashboard words its numbers (#528): ages, changes against the period
 * before, and durations. Pure, so the screen only lays them out.
 */

const HOUR_MS = 3_600_000;

/**
 * How long the oldest counted item has waited: `31 h` under two days, then
 * whole days. Coarse on purpose; a stopwatch is not what a card is for.
 */
export function ageLabel(oldestAt: string | null, now: number): string | null {
  if (oldestAt === null) return null;
  const hours = Math.max(0, Math.floor((now - Date.parse(oldestAt)) / HOUR_MS));
  return hours < 48
    ? `${String(hours)} h`
    : `${String(Math.floor(hours / 24))} d`;
}

/** `62%`, or `18.4%`: whole when it is, one place otherwise. */
export function percentLabel(value: number | null): string {
  if (value === null) return "–";
  return `${Number.isInteger(value) ? String(value) : value.toFixed(1)}%`;
}

/**
 * A median in hours as a person says it: hours under a day and a half, days
 * (one place) after.
 */
export function durationLabel(hours: number | null): string {
  if (hours === null) return "–";
  if (hours < 36) return `${String(Math.round(hours))} h`;
  return `${(hours / 24).toFixed(1)} d`;
}

export type DeltaTone = "good" | "bad" | "neutral";

export interface Delta {
  text: string;
  tone: DeltaTone;
  /** For a screen reader, where the arrow and sign are not enough. */
  spoken: string;
}

const NONE: Delta = {
  text: "no earlier figure",
  tone: "neutral",
  spoken: "no figure for the period before to compare with",
};

function signed(value: number, text: string): string {
  return value > 0 ? `+${text}` : value < 0 ? `-${text}` : text;
}

/**
 * `up` is whether a rise is an improvement: orders up is good, RTO up is bad,
 * and a payment mix has no good side at all (`null`).
 */
function toneOf(change: number, up: boolean | null): DeltaTone {
  if (up === null || change === 0) return "neutral";
  return change > 0 === up ? "good" : "bad";
}

/** A count or an amount against the period before, as a percentage. */
export function relativeDelta(
  current: number | null,
  previous: number | null,
  up: boolean | null,
): Delta {
  const change = relativeChange(current, previous);
  if (change === null) return NONE;
  const percent = Math.round(change * 100);
  const text = signed(percent, `${String(Math.abs(percent))}%`);
  return {
    text: `${text} ${arrow(percent)}`.trim(),
    tone: toneOf(percent, up),
    spoken: `${text} against the period before`,
  };
}

/** A percentage against the period before, in percentage points. */
export function pointDelta(
  current: number | null,
  previous: number | null,
  up: boolean | null,
): Delta {
  const change = pointChange(current, previous);
  if (change === null) return NONE;
  const text = signed(change, `${Math.abs(change).toFixed(1)} pt`);
  return {
    text: `${text} ${arrow(change)}`.trim(),
    tone: toneOf(change, up),
    spoken: `${text} against the period before`,
  };
}

/** A median in hours against the period before. Shorter is better. */
export function durationDelta(
  current: number | null,
  previous: number | null,
): Delta {
  if (current === null || previous === null) return NONE;
  const change = current - previous;
  const text = signed(change, durationLabel(Math.abs(change)));
  return {
    text: `${text} ${arrow(change)}`.trim(),
    tone: toneOf(change, false),
    spoken: `${text} against the period before`,
  };
}

function arrow(change: number): string {
  return change > 0 ? "▲" : change < 0 ? "▼" : "";
}

/** The shortest honest sentence for a period's comparison window. */
export function comparedWith(period: DashboardPeriod): string {
  if (period === "today") return "Compared with yesterday.";
  if (period === "mtd") return "Compared with the same days last month.";
  return `Compared with the ${period} days before.`;
}

/** Below this many shipments or orders a rate is a rumour: it is drawn grey. */
export const LOW_SAMPLE = 20;

/** `delhivery` as `Delhivery`, `ecom_express` as `Ecom Express`. */
export function courierLabel(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());
}
